import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineCheck } from 'react-icons/ai';
import { MdOutlineCancel } from 'react-icons/md';

const UpgradeDialog = ({ isOpen, onClose, onUpgrade, onAcceptRestrictions, flightData, fareData, tripType = 'roundTrip' }) => {
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  if (!isOpen) return null;

  // Determine upgrade scenario based on current fare type
  const getUpgradeScenario = () => {
    if (!fareData || !fareData.type) return { from: 'Basic Economy', to: 'Main' };
    
    switch (fareData.type) {
      case 'Basic Economy':
        return { from: 'Basic Economy', to: 'Main' };
      case 'Main':
        return { from: 'Main', to: 'Main Plus' };
      case 'Main Plus':
        return { from: 'Main Plus', to: 'Main Select' };
      default:
        return { from: fareData.type, to: 'Main Plus' };
    }
  };

  const upgradeScenario = getUpgradeScenario();

  // Calculate upgrade pricing based on scenario
  const calculateUpgradePrice = () => {
    switch (upgradeScenario.to) {
      case 'Main':
        return 76; // Basic to Main
      case 'Main Plus':
        return 122; // Main to Main Plus
      case 'Main Select':
        return 200; // Main Plus to Main Select
      default:
        return 76;
    }
  };

  const calculateTotalPrice = () => {
    if (!flightData || !fareData) return '453.18';
    
    // Calculate total based on Basic Economy price + upgrade
    const basicEconomyPrice = parseFloat((fareData.price || '$377').replace('$', '').replace(',', ''));
    const upgradePrice = calculateUpgradePrice();
    return (basicEconomyPrice + upgradePrice).toFixed(2);
  };

  // Get trip type text for display
  const getTripTypeText = () => {
    return tripType === 'oneWay' ? 'One way (non-refundable)' : 'Round trip (non-refundable)';
  };

  // Dynamic benefits based on upgrade scenario
  const getUpgradeBenefits = () => {
    switch (upgradeScenario.to) {
      case 'Main':
        return [
          { type: 'fee', text: 'Pay to check bags' },
          { type: 'checkmark', text: 'Choose a Main Cabin seat' },
          { type: 'fee', text: 'Pay for an extra legroom seat' },
          { type: 'checkmark', text: 'Board in general group (6-8)' },
          { type: 'checkmark', text: 'Changes allowed' },
          { type: 'fee', text: 'Pay to choose another same-day flight' }
        ];
      case 'Main Plus':
        return [
          { type: 'checkmark', text: 'Check 1 bag free' },
          { type: 'checkmark', text: 'Choose a Main Cabin seat' },
          { type: 'checkmark', text: 'Choose an extra legroom seat' },
          { type: 'checkmark', text: 'Board earlier with Group 5' },
          { type: 'checkmark', text: 'Changes allowed' },
          { type: 'fee', text: 'Pay to choose another same-day flight' },
          { type: 'checkmark', text: 'Full refund to travel credit' }
        ];
      case 'Main Select':
        return [
          { type: 'checkmark', text: 'Check 2 bags free' },
          { type: 'checkmark', text: 'Choose any Main Cabin seat' },
          { type: 'checkmark', text: 'Choose preferred or extra legroom seat' },
          { type: 'checkmark', text: 'Board earlier with Group 4' },
          { type: 'checkmark', text: 'Changes allowed' },
          { type: 'checkmark', text: 'Choose another same-day flight' },
          { type: 'checkmark', text: 'Full refund to travel credit' }
        ];
      default:
        return [
          { type: 'fee', text: 'Pay to check bags' },
          { type: 'checkmark', text: 'Choose a Main Cabin seat' },
          { type: 'fee', text: 'Pay for an extra legroom seat' },
          { type: 'checkmark', text: 'Board in general group (6-8)' },
          { type: 'checkmark', text: 'Changes allowed' },
          { type: 'fee', text: 'Pay to choose another same-day flight' }
        ];
    }
  };

  const upgradeBenefits = getUpgradeBenefits();

  const benefitDetails = {
    'Pay to check bags': [
      'On American Airlines operated flights, 1 personal item and 1 carry-on bag are free of charge.',
      'For travel on partner airlines within the U.S. or to / from the U.S. or Canada, the marketing airline for the first flight segment in your trip sets the bag allowance and fees for your entire trip.',
      'Learn about our checked bag policy'
    ],
    'Choose a Main Cabin seat': [
      'Seat selection is subject to availability.',
      'Fee may apply for flights operated by partner airline.',
      'Seat selection varies by the operating airline. On American Airlines operated flights, if you don\'t choose seats, we\'ll assign them at check-in free of charge. For partner-operated flights, see the airline\'s website for details.',
      'If you\'re traveling with a child and are unable to choose seats or don\'t want to pay for seats, we\'ll assign seats so children under 15 are sitting next to at least 1 adult. Families traveling'
    ],
    'Pay for an extra legroom seat': [
      'Pay for a Preferred Seat',
      'Seat selection is subject to availability.',
      'Complimentary for AAdvantage® members based on status.',
      'Fee may apply for flights operated by partner airline.',
      'Seat selection varies by the operating airline. For partner-operated flights, see the airline\'s website for details.'
    ],
    'Board in general group (6-8)': [
      'Applicable only on American Airlines operated flights.'
    ],
    'Changes allowed': [
      'Difference in ticket price may apply. Please review fare rules on the payment page for more information.'
    ],
    'Pay to choose another same-day flight': [
      'Same-day travel changes subject to availability.',
      'Learn about same-day travel changes.'
    ]
  };

  const getBenefitIcon = (type) => {
    if (type === 'checkmark') {
      return <AiOutlineCheck className="text-green-600 w-5 h-4 mt-0.5 flex-shrink-0" />;
    } else if (type === 'fee') {
      return (
        <div className="w-5 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
          <img 
            src="/content/images/farecard/cash.png" 
            alt="Fee" 
            className="w-5 h-4"
          />
        </div>
      );
    }
    return null;
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#fff]  shadow-xl max-w-[1240px] w-[1020px] max-h-[90vh] overflow-y-auto" >
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-3xl font-normal text-[#00467f]">Upgrade to {upgradeScenario.to}</h2>
          <button
            onClick={onClose}
            className="text-[#0078d2] hover:border-b-2 hover:border-[#0078d2] transition-colors"
            aria-label="Close dialog"
          >
            <MdOutlineCancel className="w-8 h-8 text-[#0078d2] font-thin"  style={{fontWeight: '100'  }}/>
          </button>
        </div>

        {/* Main Content Card */}
        <div className="px-6 pb-6">
          <div className="bg-[#ebeff0] rounded-2xl p-6 border border-gray-300" style={{border: '1px solid #364958'}}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Product Image */}
              <div className="lg:col-span-4">
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <img 
                    src="/content/images/farecard/upsell-maincabin.jpg" 
                    alt="Main Cabin Experience"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg hidden items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">Main Cabin Experience</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="lg:col-span-5">
                <h3 className="text-lg font-normal text-[#000] mb-4">{upgradeScenario.to}</h3>
                <div className="space-y-2">
                  {upgradeBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {getBenefitIcon(benefit.type)}
                      <span className="text-sm text-gray-700">{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price and Actions */}
              <div className="lg:col-span-3 text-start">
                <div className="text-start mb-6">
                  <p className="text-sm text-[#36495a] mb-2">{getTripTypeText()}</p>
                  <div className="flex items-center justify-start gap-1 mb-2">
                    <span className="text-5xl font-normal text-[#36495a]">
                      {flightData && fareData ? `+$${calculateUpgradePrice()}` : '+$30'}
                    </span>
                    <div className="text-[20px] text-[#36495a] leading-tight">
                      per<br/>person
                    </div>
                  </div>
                  <p className="text-sm text-[#36495a] w-full">
                    Total <span>{flightData && fareData ? `$${calculateTotalPrice()}` : '$453.18'}</span>
                    <span className="text-xs ">(all passengers)</span>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={onUpgrade}
                    className="w-full bg-[#0078d2] hover:bg-[#0061ab] text-white font-medium py-3 px-6 rounded transition-colors text-lg"
                  >
                    <span className="hover:underline">Move to {upgradeScenario.to}</span>
                  </button>
                  <button
                    onClick={onAcceptRestrictions}
                    className="w-full border-2 border-[#0078d2] text-[#0078d2] hover:bg-[#F5FAE1] hover:text-[#0078d2] font-medium py-3 px-6 rounded transition-colors text-lg"
                  >
                    <span className="hover:underline">Accept restrictions</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information Toggle - Below the card */}
        <div className="px-6 pb-4">
          <button
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg 
              className={`w-5 h-5 transition-transform ${showAdditionalInfo ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span className="font-medium text-[#0860c4] text-base">Additional information</span>
            <span className="sr-only">{showAdditionalInfo ? 'expanded' : 'collapsed'}</span>
          </button>
        </div>

        {/* Additional Information Expanded - Below the toggle */}
        {showAdditionalInfo && (
          <div className="px-8 pb-6">
            <div className="bg-white border-t border-gray-200 pt-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">{upgradeScenario.to}</h3>
              
              {upgradeBenefits.map((benefit, index) => (
                <div key={index} className="mb-6">
                  <div className="font-semibold text-[#36495a] mb-2">{benefit.text}</div>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-[#36495a] leading-tight font-[480] ml-4">
                    {benefitDetails[benefit.text]?.map((detail, detailIndex) => (
                      <li key={detailIndex} className="leading-relaxed">
                        {detail.includes('Learn about our') ? (
                          <>
                            {detail.split('Learn about our')[0]}
                            <span className="text-[#0860c4] underline cursor-pointer hover:text-[#0860c4]">
                              Learn about our {detail.split('Learn about our')[1]}
                              <svg className="inline w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </span>
                          </>
                        ) : detail.includes('Families traveling') ? (
                          <>
                            {detail.split('Families traveling')[0]}
                            <span className="text-[#0860c4] underline cursor-pointer hover:text-[#0860c4]">
                              Families traveling
                              <svg className="inline w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </span>
                          </>
                        ) : detail.includes('same-day travel changes') ? (
                          <>
                            {detail.split('same-day travel changes')[0]}
                            <span className="text-[#0860c4] underline cursor-pointer hover:text-[#0860c4]">
                              same-day travel changes
                              <svg className="inline w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </span>
                          </>
                        ) : (
                          detail
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default UpgradeDialog;